import React, {useCallback, useEffect, useMemo, useState} from "react";
import {css} from "@emotion/react";
import type {LatLngExpression} from "leaflet";

import {Button} from "@pg-design/button-module";
import {Checkbox} from "@pg-design/checkbox-module";
import {borderRadius, calculateRemSize, mr, mt, pb, pt, pv} from "@pg-design/helpers-css-strings";
import {Loader} from "@pg-design/loader-module";
import {getThemeBreakpoint, getThemeVariable} from "@pg-design/styles-strings";
import {AdvancedScroll} from "@pg-mono/advanced-scroll";
import {FormActions} from "@pg-mono/form";
import {noop} from "@pg-mono/nodash";
import {convertToArrayOfLatLngLiterals, IPolyline, LazyOpenStreetMap} from "@pg-mono/open-street-map";

import {InteractiveRegionsMapLayer} from "../../../maps/components/InteractiveRegionsMapLayer";
import {useMapRegionDistrictsAndSurroundings} from "../../../offer/map/hooks/use_map_region_districts_and_surroundings";
import {IRegionListMap} from "../../../region/types/IRegionListMap";
import {RegionType} from "../../../region/types/RegionType";
import {SearchTab} from "../../actions/fetch_search_all_action";
import {searchFormFields} from "../../constants/search_form_constants";
import {useSearchInteractiveRegionSelection} from "../../hooks/use_search_interactive_region_selection";
import {SearchFormValuesType} from "../../types/SearchFormValuesType";
import {ISearchInputRegion, ISearchInputValue} from "../ISearchInputValue";
import {SearchDistanceFilterMobile} from "../SearchDistanceFilterMobile";

interface IProps {
    defaultDistanceValue: number;
    formValues: SearchFormValuesType;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formActions?: FormActions<Record<keyof typeof searchFormFields, any>>;
    search: ISearchInputValue;
    isInModal?: boolean;
    showMainRegionMapOutline?: boolean;
    updateSelectedRegions: (option: ISearchInputValue) => void;
    updateRegionsImmediately?: boolean;
}

export const SearchInteractiveRegionsMap = (props: IProps) => {
    const [regionsToHover, setRegionsToHover] = useState<number[]>([]);

    const searchFormRegions = props.search.tabType === SearchTab.Regions ? props.search.regions : null;

    const {data: availableDistrictsAndSurroundings, regions} = useMapRegionDistrictsAndSurroundings({
        selectedRegions: searchFormRegions
    });

    const {
        selectedRegionIdsState,
        selectedRegionIds,
        hasAllDistrictBeenSelected,
        handleSelectedRegionsChange,
        toggleSelectedDistrict,
        syncSelectedRegionsWithRemoteSelection
    } = useSearchInteractiveRegionSelection({
        regions,
        allAvailableRegions: availableDistrictsAndSurroundings?.results || []
    });

    useEffect(() => {
        if (selectedRegionIds.length === 0) {
            return;
        }

        if (regions.districts.length === selectedRegionIds.length && regions.districts.every((item) => selectedRegionIds.includes(item.id))) {
            props.formActions?.update({
                distance: 5
            });
        } else {
            props.formActions?.update({
                distance: 0
            });
        }
    }, [selectedRegionIds]);

    useEffect(() => {
        if (searchFormRegions && regions.main?.id && availableDistrictsAndSurroundings) {
            syncSelectedRegionsWithRemoteSelection(searchFormRegions);
        }
    }, [searchFormRegions, regions.main?.id, availableDistrictsAndSurroundings]);

    useEffect(() => {
        if (props.updateRegionsImmediately) {
            handleSubmitRegions();
        }
    }, [selectedRegionIds]);

    const handleSubmitRegions = (paramsToUpdate?: Partial<SearchFormValuesType>) => {
        if (!availableDistrictsAndSurroundings) {
            return;
        }

        let selectedRegions = availableDistrictsAndSurroundings.results.filter((item) => selectedRegionIds.includes(item.id));

        if (hasAllDistrictBeenSelected && regions.main) {
            selectedRegions = [...selectedRegions.filter((item) => item.type !== RegionType.DISTRICT), regions.main];
        }

        props.updateSelectedRegions({
            ...props.search,
            tabType: SearchTab.Regions,
            regions: selectedRegions.map((item) => {
                const newItem: ISearchInputRegion = {
                    ...item,
                    full_name: item.short_name,
                    is_leaf_node: false,
                    name: item.short_name,
                    parent: {id: item.type === RegionType.DISTRICT || item.type === RegionType.SURROUNDINGS ? regions.main?.id || 0 : 0}
                };
                return newItem;
            }),
            ...(paramsToUpdate ? paramsToUpdate : {})
        });
    };

    const onRegionClick = useCallback(
        (regionId: number) => {
            handleSelectedRegionsChange(regionId);
        },
        [handleSelectedRegionsChange]
    );

    const handleCheckboxMouseEnter = (region: IRegionListMap) => {
        setRegionsToHover([region.id]);
    };
    const handleCheckboxMouseLeave = (region: IRegionListMap) => {
        setRegionsToHover((prev) =>
            prev.filter((id) => {
                return id !== region.id;
            })
        );
    };

    const renderDistrictCheckboxes = () => {
        return (
            <>
                {regions.districts.length > 0 && (
                    <>
                        <Checkbox
                            checkedMarkColor="#02D054"
                            id="all_districts_checkbox"
                            name="all_districts_checkbox"
                            labelContent="Wszystkie dzielnice"
                            checked={hasAllDistrictBeenSelected}
                            onChange={toggleSelectedDistrict}
                            value="all"
                        />
                        {regions.districts.map((item) => {
                            const checked = selectedRegionIds.includes(item.id);
                            const key = `region_${item.id}`;

                            return (
                                <Checkbox
                                    key={key}
                                    id={key}
                                    checkedMarkColor={hasAllDistrictBeenSelected ? "rgba(2, 208, 84, 0.2)" : "#02D054"}
                                    name={item.name_declension_what}
                                    labelContent={item.short_name.split(", ").pop()}
                                    checked={checked || hasAllDistrictBeenSelected}
                                    onChange={() => {
                                        handleSelectedRegionsChange(item.id);
                                    }}
                                    onMouseEnter={() => {
                                        handleCheckboxMouseEnter(item);
                                    }}
                                    onMouseLeave={() => {
                                        handleCheckboxMouseLeave(item);
                                    }}
                                    value={item.id}
                                    css={checkboxWrapperStyle}
                                />
                            );
                        })}
                    </>
                )}

                {regions.surroundings.length > 0 && (
                    <div css={[surroundingsWrapperStyle, regions.districts.length > 0 && surroundingsDistrictSeparatorStyle]}>
                        {regions.surroundings.map((item) => {
                            const checked = selectedRegionIds.includes(item.id);
                            const key = `region_${item.id}`;

                            return (
                                <Checkbox
                                    key={key}
                                    id={key}
                                    checkedMarkColor="#02D054"
                                    name={item.name_declension_what}
                                    labelContent={item.short_name.split(", ").pop()}
                                    checked={checked}
                                    onChange={() => {
                                        handleSelectedRegionsChange(item.id);
                                    }}
                                    onMouseEnter={() => {
                                        handleCheckboxMouseEnter(item);
                                    }}
                                    onMouseLeave={() => {
                                        handleCheckboxMouseLeave(item);
                                    }}
                                    value={item.id}
                                    css={checkboxWrapperStyle}
                                />
                            );
                        })}
                    </div>
                )}
            </>
        );
    };

    const polylines: IPolyline[] | undefined = useMemo(() => {
        return props.showMainRegionMapOutline && regions.main
            ? [
                  {
                      id: regions.main.id.toString(),
                      positions: convertToArrayOfLatLngLiterals(regions.main?.area.geo_area.coordinates, {reversedValues: true}).flat(
                          Infinity
                      ) as LatLngExpression[],
                      pane: "topPane",
                      pathOptions: {
                          color: "rgba(0, 0, 0, 0.3)",
                          weight: 1
                      }
                  }
              ]
            : undefined;
    }, [regions.main?.id, props.showMainRegionMapOutline]);

    return (
        <div css={wrapperStyle}>
            <div css={regionsWrapperStyle}>
                <div css={[mapColumnStyle, props.isInModal && modalDesktopColumnStyle]}>
                    <React.Suspense
                        fallback={
                            <div css={loaderWrapperStyle}>
                                <Loader size="lg" />
                            </div>
                        }
                    >
                        {availableDistrictsAndSurroundings ? (
                            <LazyOpenStreetMap polylines={polylines} css={mapStyle} fitBounds>
                                <InteractiveRegionsMapLayer
                                    regionsToHover={regionsToHover}
                                    staticRegions={regions.main ? [regions.main] : []}
                                    regions={availableDistrictsAndSurroundings.results}
                                    hasAllDistrictBeenSelected={hasAllDistrictBeenSelected}
                                    selectedFeaturesState={[selectedRegionIdsState[0], noop]}
                                    onRegionClick={onRegionClick}
                                />
                            </LazyOpenStreetMap>
                        ) : null}
                    </React.Suspense>
                    <div css={distanceFilterWrapperStyle}>
                        <SearchDistanceFilterMobile
                            defaultValue={props.defaultDistanceValue}
                            values={props.formValues}
                            onAfterChange={() => null}
                            onChange={(value) => {
                                if (props.updateRegionsImmediately) {
                                    handleSubmitRegions(value);
                                    return;
                                }
                                props.formActions?.update(value);
                            }}
                            onSubmit={() => null}
                            setDesktopTypeBlendActive={() => null}
                            menuPlacement="top"
                        />
                    </div>
                </div>
                <div css={[regionListColumnStyle, props.isInModal && modalDesktopColumnStyle]}>
                    {props.isInModal ? (
                        renderDistrictCheckboxes()
                    ) : (
                        <AdvancedScroll contentStyle={regionListScrollWrapperStyle} height={560}>
                            {renderDistrictCheckboxes()}
                        </AdvancedScroll>
                    )}
                </div>
            </div>
            {!props.updateRegionsImmediately && (
                <div css={buttonWrapperStyle}>
                    <Button type="button" size="small" variant="filled_primary" onClick={handleSubmitRegions} css={buttonStyle}>
                        Zatwierdź
                    </Button>
                </div>
            )}
        </div>
    );
};

const wrapperStyle = css`
    display: flex;
    flex-direction: column;
`;

const regionsWrapperStyle = css`
    display: flex;
    flex-direction: column;

    @media (min-width: ${getThemeBreakpoint().md}) {
        flex-direction: row;
        gap: ${calculateRemSize(2)};
    }
`;

const buttonWrapperStyle = css`
    ${pt(3)};

    @media (min-width: ${getThemeBreakpoint().md}) {
        ${pt(2)};
    }
`;

const buttonStyle = css`
    width: 100%;
`;

const distanceFilterWrapperStyle = css`
    position: absolute;
    bottom: ${calculateRemSize(1)};
    left: ${calculateRemSize(1)};
    background: #fff;
    border: 1px solid ${getThemeVariable("colors.gray.600")};
    z-index: 1000; // should be higher than map
    ${borderRadius(1)};
`;

const mapColumnStyle = css`
    flex: 1 1 100%;
    position: relative;
`;

const mapStyle = css`
    width: 100%;
    height: 380px;
    position: initial;

    @media (min-width: ${getThemeBreakpoint().md}) {
        height: 560px;
    }
`;

const loaderWrapperStyle = css`
    height: 380px;
    display: flex;
    justify-content: center;
    align-items: center;

    @media (min-width: ${getThemeBreakpoint().md}) {
        height: 560px;
    }
`;

const regionListColumnStyle = css`
    ${pv(3)};
    flex: 1 1 100%;
    display: flex;
    flex-direction: column;
    gap: ${calculateRemSize(2)};

    @media (min-width: ${getThemeBreakpoint().md}) {
        ${pv(0)};
        flex: 0 0 23rem;
        gap: ${calculateRemSize(1)};
    }
`;

const modalDesktopColumnStyle = css`
    @media (min-width: ${getThemeBreakpoint().md}) {
        max-height: 56rem;
        overflow: auto;
    }
`;

const regionListScrollWrapperStyle = css`
    display: flex;
    flex-direction: column;
    gap: ${calculateRemSize(2)};

    @media (min-width: ${getThemeBreakpoint().md}) {
        flex: 0 0 23rem;
        gap: ${calculateRemSize(1)};
    }
`;

const surroundingsWrapperStyle = css`
    ${pb(1)};
    ${mr(2)};
    text-align: left;
`;

const surroundingsDistrictSeparatorStyle = css`
    ${pt(1)};
    ${mt(1)};
    border-top: 1px solid ${getThemeVariable("colors.gray.400")};
`;

const checkboxWrapperStyle = css`
    align-items: flex-start;
    width: 100%;
    line-height: ${calculateRemSize(3)};
    text-align: left;
`;
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {css} from "@emotion/react";
import type {LatLngExpression} from "leaflet";

import {Button} from "@pg-design/button-module";
import {Checkbox} from "@pg-design/checkbox-module";
import {borderRadius, calculateRemSize, mr, mt, pb, pt, pv} from "@pg-design/helpers-css-strings";
import {Loader} from "@pg-design/loader-module";
import {getThemeBreakpoint, getThemeVariable} from "@pg-design/styles-strings";
import {AdvancedScroll} from "@pg-mono/advanced-scroll";
import {FormActions} from "@pg-mono/form";
import {noop} from "@pg-mono/nodash";
import {convertToArrayOfLatLngLiterals, IPolyline, LazyOpenStreetMap} from "@pg-mono/open-street-map";

import {InteractiveRegionsMapLayer} from "../../../maps/components/InteractiveRegionsMapLayer";
import {useMapRegionDistrictsAndSurroundings} from "../../../offer/map/hooks/use_map_region_districts_and_surroundings";
import {IRegionListMap} from "../../../region/types/IRegionListMap";
import {RegionType} from "../../../region/types/RegionType";
import {SearchTab} from "../../actions/fetch_search_all_action";
import {searchFormFields} from "../../constants/search_form_constants";
import {useSearchInteractiveRegionSelection} from "../../hooks/use_search_interactive_region_selection";
import {SearchFormValuesType} from "../../types/SearchFormValuesType";
import {ISearchInputRegion, ISearchInputValue} from "../ISearchInputValue";
import {SearchDistanceFilterMobile} from "../SearchDistanceFilterMobile";

interface IProps {
    defaultDistanceValue: number;
    formValues: SearchFormValuesType;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formActions?: FormActions<Record<keyof typeof searchFormFields, any>>;
    search: ISearchInputValue;
    isInModal?: boolean;
    showMainRegionMapOutline?: boolean;
    updateSelectedRegions: (option: ISearchInputValue) => void;
    updateRegionsImmediately?: boolean;
}

export const SearchInteractiveRegionsMap = (props: IProps) => {
    const [regionsToHover, setRegionsToHover] = useState<number[]>([]);

    const searchFormRegions = props.search.tabType === SearchTab.Regions ? props.search.regions : null;

    const {data: availableDistrictsAndSurroundings, regions} = useMapRegionDistrictsAndSurroundings({
        selectedRegions: searchFormRegions
    });

    const {
        selectedRegionIdsState,
        selectedRegionIds,
        hasAllDistrictBeenSelected,
        handleSelectedRegionsChange,
        toggleSelectedDistrict,
        syncSelectedRegionsWithRemoteSelection
    } = useSearchInteractiveRegionSelection({
        regions,
        allAvailableRegions: availableDistrictsAndSurroundings?.results || []
    });

    useEffect(() => {
        if (selectedRegionIds.length === 0) {
            return;
        }

        if (regions.districts.length === selectedRegionIds.length && regions.districts.every((item) => selectedRegionIds.includes(item.id))) {
            props.formActions?.update({
                distance: 5
            });
        } else {
            props.formActions?.update({
                distance: 0
            });
        }
    }, [selectedRegionIds]);

    useEffect(() => {
        if (searchFormRegions && regions.main?.id && availableDistrictsAndSurroundings) {
            syncSelectedRegionsWithRemoteSelection(searchFormRegions);
        }
    }, [searchFormRegions, regions.main?.id, availableDistrictsAndSurroundings]);

    useEffect(() => {
        if (props.updateRegionsImmediately) {
            handleSubmitRegions();
        }
    }, [selectedRegionIds]);

    const handleSubmitRegions = (paramsToUpdate?: Partial<SearchFormValuesType>) => {
        if (!availableDistrictsAndSurroundings) {
            return;
        }

        let selectedRegions = availableDistrictsAndSurroundings.results.filter((item) => selectedRegionIds.includes(item.id));

        if (hasAllDistrictBeenSelected && regions.main) {
            selectedRegions = [...selectedRegions.filter((item) => item.type !== RegionType.DISTRICT), regions.main];
        }

        props.updateSelectedRegions({
            ...props.search,
            tabType: SearchTab.Regions,
            regions: selectedRegions.map((item) => {
                const newItem: ISearchInputRegion = {
                    ...item,
                    full_name: item.short_name,
                    is_leaf_node: false,
                    name: item.short_name,
                    parent: {id: item.type === RegionType.DISTRICT || item.type === RegionType.SURROUNDINGS ? regions.main?.id || 0 : 0}
                };
                return newItem;
            }),
            ...(paramsToUpdate ? paramsToUpdate : {})
        });
    };

    const onRegionClick = useCallback(
        (regionId: number) => {
            handleSelectedRegionsChange(regionId);
        },
        [handleSelectedRegionsChange]
    );

    const handleCheckboxMouseEnter = (region: IRegionListMap) => {
        setRegionsToHover([region.id]);
    };
    const handleCheckboxMouseLeave = (region: IRegionListMap) => {
        setRegionsToHover((prev) =>
            prev.filter((id) => {
                return id !== region.id;
            })
        );
    };

    const renderDistrictCheckboxes = () => {
        return (
            <>
                {regions.districts.length > 0 && (
                    <>
                        <Checkbox
                            checkedMarkColor="#02D054"
                            id="all_districts_checkbox"
                            name="all_districts_checkbox"
                            labelContent="Wszystkie dzielnice"
                            checked={hasAllDistrictBeenSelected}
                            onChange={toggleSelectedDistrict}
                            value="all"
                        />
                        {regions.districts.map((item) => {
                            const checked = selectedRegionIds.includes(item.id);
                            const key = `region_${item.id}`;

                            return (
                                <Checkbox
                                    key={key}
                                    id={key}
                                    checkedMarkColor={hasAllDistrictBeenSelected ? "rgba(2, 208, 84, 0.2)" : "#02D054"}
                                    name={item.name_declension_what}
                                    labelContent={item.short_name.split(", ").pop()}
                                    checked={checked || hasAllDistrictBeenSelected}
                                    onChange={() => {
                                        handleSelectedRegionsChange(item.id);
                                    }}
                                    onMouseEnter={() => {
                                        handleCheckboxMouseEnter(item);
                                    }}
                                    onMouseLeave={() => {
                                        handleCheckboxMouseLeave(item);
                                    }}
                                    value={item.id}
                                    css={checkboxWrapperStyle}
                                />
                            );
                        })}
                    </>
                )}

                {regions.surroundings.length > 0 && (
                    <div css={[surroundingsWrapperStyle, regions.districts.length > 0 && surroundingsDistrictSeparatorStyle]}>
                        {regions.surroundings.map((item) => {
                            const checked = selectedRegionIds.includes(item.id);
                            const key = `region_${item.id}`;

                            return (
                                <Checkbox
                                    key={key}
                                    id={key}
                                    checkedMarkColor="#02D054"
                                    name={item.name_declension_what}
                                    labelContent={item.short_name.split(", ").pop()}
                                    checked={checked}
                                    onChange={() => {
                                        handleSelectedRegionsChange(item.id);
                                    }}
                                    onMouseEnter={() => {
                                        handleCheckboxMouseEnter(item);
                                    }}
                                    onMouseLeave={() => {
                                        handleCheckboxMouseLeave(item);
                                    }}
                                    value={item.id}
                                    css={checkboxWrapperStyle}
                                />
                            );
                        })}
                    </div>
                )}
            </>
        );
    };

    const polylines: IPolyline[] | undefined = useMemo(() => {
        return props.showMainRegionMapOutline && regions.main
            ? [
                  {
                      id: regions.main.id.toString(),
                      positions: convertToArrayOfLatLngLiterals(regions.main?.area.geo_area.coordinates, {reversedValues: true}).flat(
                          Infinity
                      ) as LatLngExpression[],
                      pane: "topPane",
                      pathOptions: {
                          color: "rgba(0, 0, 0, 0.3)",
                          weight: 1
                      }
                  }
              ]
            : undefined;
    }, [regions.main?.id, props.showMainRegionMapOutline]);

    return (
        <div css={wrapperStyle}>
            <div css={regionsWrapperStyle}>
                <div css={[mapColumnStyle, props.isInModal && modalDesktopColumnStyle]}>
                    <React.Suspense
                        fallback={
                            <div css={loaderWrapperStyle}>
                                <Loader size="lg" />
                            </div>
                        }
                    >
                        {availableDistrictsAndSurroundings ? (
                            <LazyOpenStreetMap polylines={polylines} css={mapStyle} fitBounds>
                                <InteractiveRegionsMapLayer
                                    regionsToHover={regionsToHover}
                                    staticRegions={regions.main ? [regions.main] : []}
                                    regions={availableDistrictsAndSurroundings.results}
                                    hasAllDistrictBeenSelected={hasAllDistrictBeenSelected}
                                    selectedFeaturesState={[selectedRegionIdsState[0], noop]}
                                    onRegionClick={onRegionClick}
                                />
                            </LazyOpenStreetMap>
                        ) : null}
                    </React.Suspense>
                    <div css={distanceFilterWrapperStyle}>
                        <SearchDistanceFilterMobile
                            defaultValue={props.defaultDistanceValue}
                            values={props.formValues}
                            onAfterChange={() => null}
                            onChange={(value) => {
                                if (props.updateRegionsImmediately) {
                                    handleSubmitRegions(value);
                                    return;
                                }
                                props.formActions?.update(value);
                            }}
                            onSubmit={() => null}
                            setDesktopTypeBlendActive={() => null}
                            menuPlacement="top"
                        />
                    </div>
                </div>
                <div css={[regionListColumnStyle, props.isInModal && modalDesktopColumnStyle]}>
                    {props.isInModal ? (
                        renderDistrictCheckboxes()
                    ) : (
                        <AdvancedScroll contentStyle={regionListScrollWrapperStyle} height={560}>
                            {renderDistrictCheckboxes()}
                        </AdvancedScroll>
                    )}
                </div>
            </div>
            {!props.updateRegionsImmediately && (
                <div css={buttonWrapperStyle}>
                    <Button type="button" size="small" variant="filled_primary" onClick={handleSubmitRegions} css={buttonStyle}>
                        Zatwierdź
                    </Button>
                </div>
            )}
        </div>
    );
};

const wrapperStyle = css`
    display: flex;
    flex-direction: column;
`;

const regionsWrapperStyle = css`
    display: flex;
    flex-direction: column;

    @media (min-width: ${getThemeBreakpoint().md}) {
        flex-direction: row;
        gap: ${calculateRemSize(2)};
    }
`;

const buttonWrapperStyle = css`
    ${pt(3)};

    @media (min-width: ${getThemeBreakpoint().md}) {
        ${pt(2)};
    }
`;

const buttonStyle = css`
    width: 100%;
`;

const distanceFilterWrapperStyle = css`
    position: absolute;
    bottom: ${calculateRemSize(1)};
    left: ${calculateRemSize(1)};
    background: #fff;
    border: 1px solid ${getThemeVariable("colors.gray.600")};
    z-index: 1000; // should be higher than map
    ${borderRadius(1)};
`;

const mapColumnStyle = css`
    flex: 1 1 100%;
    position: relative;
`;

const mapStyle = css`
    width: 100%;
    height: 380px;
    position: initial;

    @media (min-width: ${getThemeBreakpoint().md}) {
        height: 560px;
    }
`;

const loaderWrapperStyle = css`
    height: 380px;
    display: flex;
    justify-content: center;
    align-items: center;

    @media (min-width: ${getThemeBreakpoint().md}) {
        height: 560px;
    }
`;

const regionListColumnStyle = css`
    ${pv(3)};
    flex: 1 1 100%;
    display: flex;
    flex-direction: column;
    gap: ${calculateRemSize(2)};

    @media (min-width: ${getThemeBreakpoint().md}) {
        ${pv(0)};
        flex: 0 0 23rem;
        gap: ${calculateRemSize(1)};
    }
`;

const modalDesktopColumnStyle = css`
    @media (min-width: ${getThemeBreakpoint().md}) {
        max-height: 56rem;
        overflow: auto;
    }
`;

const regionListScrollWrapperStyle = css`
    display: flex;
    flex-direction: column;
    gap: ${calculateRemSize(2)};

    @media (min-width: ${getThemeBreakpoint().md}) {
        flex: 0 0 23rem;
        gap: ${calculateRemSize(1)};
    }
`;

const surroundingsWrapperStyle = css`
    ${pb(1)};
    ${mr(2)};
    text-align: left;
`;

const surroundingsDistrictSeparatorStyle = css`
    ${pt(1)};
    ${mt(1)};
    border-top: 1px solid ${getThemeVariable("colors.gray.400")};
`;

const checkboxWrapperStyle = css`
    align-items: flex-start;
    width: 100%;
    line-height: ${calculateRemSize(3)};
    text-align: left;
`;
